import React, { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";

const expiredMessage = "Enrollment code expired.";
const record = value => value !== null && typeof value === "object" && !Array.isArray(value);

function generationError(error) {
	const code = typeof error?.code === "string" ? error.code.replace(/^functions\//, "") : "";
	switch (code) {
		case "permission-denied":
		case "unauthenticated":
			return "Sign in with an authorized support account.";
		case "resource-exhausted":
			return "Enrollment code limit reached. Wait 10 minutes and try again.";
		case "not-found":
			return "Restaurant or enrollment service unavailable. Check the restaurant and environment.";
		default:
			return "Code generation unconfirmed. An unseen code may exist; it expires after 10 minutes.";
	}
}

function readEnrollment(value, restaurantId, projectId) {
	if (!record(value) || value.success !== true || value.restaurantId !== restaurantId || value.projectId !== projectId ||
		typeof value.enrollmentCode !== "string" || !/^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/.test(value.enrollmentCode) ||
		typeof value.enrollmentId !== "string" || !/^[a-f0-9]{64}$/.test(value.enrollmentId) ||
		!Number.isSafeInteger(value.expiresAt) || value.expiresAt <= 0 || value.expiresAt > 8640000000000000) {
		throw new Error("Invalid enrollment code response");
	}
	return { enrollmentCode: value.enrollmentCode, enrollmentId: value.enrollmentId, expiresAt: value.expiresAt };
}

// The parent keys this component by restaurant, environment, and auth session.
export default function StaffDeviceEnrollmentCode({ restaurantId, projectId, instance, label, disabled, isCurrent, replacesDeviceId = null }) {
	const [enrollment, setEnrollment] = useState(null);
	const [now, setNow] = useState(Date.now);
	const [operation, setOperation] = useState("");
	const [message, setMessage] = useState("");
	const alive = useRef(false), request = useRef(0), pending = useRef(false);
	useEffect(() => {
		request.current++; alive.current = true;
		return () => { alive.current = false; };
	}, []);
	const clearCode = useCallback(status => {
		// Invalidate pending UI outcomes, without canceling anything on the server.
		request.current++; pending.current = false;
		setEnrollment(null); setOperation(""); setMessage(status);
	}, []);
	useEffect(() => {
		if (!enrollment) return undefined;
		let timer;
		const tick = () => {
			if (!isCurrent()) { clearCode(""); return; }
			const currentTime = Date.now();
			if (currentTime >= enrollment.expiresAt) { clearCode(expiredMessage); return; }
			setNow(currentTime);
			timer = setTimeout(tick, Math.min(1000, enrollment.expiresAt - currentTime));
		};
		tick();
		return () => clearTimeout(timer);
	}, [enrollment, isCurrent, clearCode]);

	const execute = async cancel => {
		if (disabled || pending.current || !isCurrent() || !alive.current) return;
		const deviceLabel = label.trim();
		if (cancel ? !enrollment : enrollment || !deviceLabel || deviceLabel.length > 80) return;
		if (cancel && enrollment.expiresAt <= Date.now()) { clearCode(expiredMessage); return; }
		const currentRequest = ++request.current;
		const active = () => alive.current && request.current === currentRequest && isCurrent();
		pending.current = true; setOperation(cancel ? "cancel" : "generate"); setMessage("");
		try {
			const result = await httpsCallable(instance, cancel ? "cancelScervStaffDeviceEnrollmentCode" : "generateScervStaffDeviceEnrollmentCode")(
				cancel ? { restaurantId, enrollmentId: enrollment.enrollmentId } :
					{ restaurantId, label: deviceLabel, ...(replacesDeviceId ? { replacesDeviceId } : {}) });
			if (!active()) return;
			if (cancel) {
				if (!record(result.data) || result.data.success !== true || typeof result.data.changed !== "boolean") {
					throw new Error("Unconfirmed cancellation");
				}
				setEnrollment(null);
				setMessage(result.data.changed ? "Enrollment code canceled." : "No active enrollment code was canceled.");
			} else {
				const code = readEnrollment(result.data, restaurantId, projectId);
				const currentTime = Date.now();
				if (code.expiresAt <= currentTime) { setMessage(expiredMessage); return; }
				setNow(currentTime); setEnrollment({ ...code, label: deviceLabel, replacing: !!replacesDeviceId });
			}
		} catch (error) {
			if (active()) setMessage(cancel ? "Cancellation unconfirmed. The code may still be active until it expires." :
				generationError(error));
		} finally {
			if (active()) { pending.current = false; setOperation(""); }
		}
	};
	const remaining = enrollment ? Math.max(0, Math.ceil((enrollment.expiresAt - now) / 1000)) : 0;
	return <div className="staff-device-enrollment-code" aria-label="Staff device enrollment code">
		<button type="button" disabled={disabled || !!operation || !!enrollment || !label.trim() || label.trim().length > 80}
			onClick={() => execute(false)}>Generate enrollment code</button>
		{operation && <p role="status">{operation === "generate" ? "Generating enrollment code..." : "Canceling enrollment code..."}</p>}
		{message && <p role="status">{message}</p>}
		{enrollment && <div className="staff-device-code-result">
			<p>{enrollment.label}{enrollment.replacing ? " (replaces the old enrollment when used)" : ""}</p>
			<label>Enrollment code <input readOnly autoComplete="off" spellCheck={false} value={enrollment.enrollmentCode}
				style={{ width: "12ch", maxWidth: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: "1.5rem" }} /></label>
			<p>Expires <time dateTime={new Date(enrollment.expiresAt).toISOString()}>{new Date(enrollment.expiresAt).toLocaleTimeString()}</time>
				{` (${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")} remaining)`}</p>
			<button type="button" disabled={disabled || !!operation} onClick={() => execute(true)}>Cancel enrollment code</button>
			<button type="button" onClick={() => clearCode("Code dismissed. Any active code expires automatically.")}>Dismiss code</button>
		</div>}
	</div>;
}
